import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { InfoCircleOutlined, QuestionOutlined } from '@ant-design/icons';
import { Flex, Button, Icon as FAIcon } from 'ui';
import { Tooltip, Progress } from 'antd';

const Container = styled(Flex)`
  background-color: #C5DBE9;
  ${p => !p.isFirst && 'border-top: 1px solid #fff'};
  ${p => !p.isLast && 'border-bottom: 1px solid #fff'};
  min-height: 40px;
  height: auto;
  padding: 6px;
  user-select: none;
  display: ${p => p.visible ? 'flex' : 'none'};
  width: 100%;
`;

const Buttons = styled(Flex)`
  height: 22px !important;
  width: calc(100% - 300px);
  > :last-child {
    margin-right: 5px !important;
  }
`;

const InfoIcon = styled(InfoCircleOutlined)`
  background-color: #fff;
  border-radius: 50%;
  color: #0098cc;
  font-size: 18px;
  margin-left: 5px;
`;

const ButtonIcon = styled(QuestionOutlined)`
  color: ${p => p.color};
  margin-right: ${p => p.marginRight || 0}px;
`;

const UndoIcon = styled(FAIcon)`
  color: ${p => p.color};
  font-size: 12px;
  margin-right: 3px;
`;

const Undo = styled.div`
  color: #0098cc;
  text-decoration: underline;
  cursor: pointer;
  min-width: 60px;
  margin-right: 10px;
  display: inline-block;
  width: ${p => p.width}px;
  text-align: center;
`;

const Answer = styled.div`
  color: #9aa7ba;
  min-width: 60px;
  margin-right: 10px;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 14px;
  display: inline-block;
  width: ${p => p.width}px;
  text-align: center;
`;

const StyledButton = styled(Button)`
  background-color: #fff;
  border-radius: 5px;
  height: 100% !important;
  border: 1px solid ${p => p.color};
  text-transform: uppercase;
  font-weight: bold;
  width: auto;
  min-width: 60px;
  margin-right: ${p => p.marginRight}px;
  padding: 0 5px;
`;

const Question = styled.div`
  width: 300px;
`;

const TooltipContainer = styled.div`
  display: inline-block;
`;

const QuestionBlock = ({
  intl,
  question,
  description,
  handleQuestionAnswer,
  handleQuestionUndo,
  removeQuestion,
  undoTime = 3000,
  yes,
  no,
  id,
  stopTimer,
  setQuestions,
  unknown,
  hasUnknown = false,
  resetTags,
  visible,
  isFirst = false,
  isLast = false,
  canUndo = false,
}) => {
  const [answer, setAnswer] = useState();
  const [timerPercent, setTimerPercent] = useState(0);
  const [timer, setTimer] = useState();
  const [intervalState, setIntervalState] = useState();
  const [yesWidth, setYesWidth] = useState();
  const [noWidth, setNoWidth] = useState();
  const [unknownWidth, setUnknownWidth] = useState();
  const yesRef = useRef(null);
  const noRef = useRef(null);
  const unknownRef = useRef(null);

  useEffect(() => () => {
    if (!hasUnknown) {
      clearTimeout(timer);
      clearInterval(intervalState);
      setTimerPercent(0);
    }
    setAnswer();
  }, []);

  useEffect(() => {
    if (!yesWidth && yesRef?.current?.clientWidth) {
      setYesWidth(yesRef?.current?.clientWidth);
    }
  }, [yesRef]);

  useEffect(() => {
    if (!noWidth && noRef?.current?.clientWidth) {
      setNoWidth(noRef?.current?.clientWidth);
    }
  }, [noRef]);

  useEffect(() => {
    if (!unknownWidth && unknownRef?.current?.clientWidth) {
      setUnknownWidth(unknownRef?.current?.clientWidth);
    }
  }, [unknownRef]);

  const handleAnswer = (value) => {
    handleQuestionAnswer(value);
    setAnswer(value);
    if (canUndo) {
      if (!hasUnknown) {
        setTimerPercent(0);
        setIntervalState(setInterval(
          () => setTimerPercent(p => ((100 / undoTime) * 100) + p)
          , 100));
        setTimer(setTimeout(() => {
          clearInterval(intervalState);
          setTimerPercent(0);
          removeQuestion(id);
          setAnswer();
        }, undoTime));
      }
    } else {
      removeQuestion(id);
      setAnswer();
    }
  };

  const handleUndo = () => {
    if (!hasUnknown && canUndo) {
      clearTimeout(timer);
      clearInterval(intervalState);
      setTimerPercent(0);
    }
    handleQuestionUndo(answer);
    setAnswer();
  };

  useEffect(() => {
    if (stopTimer) {
      handleUndo();
      setQuestions(state =>
        state?.map((q) => {
          if (q.id === id) {
            return {
              ...q,
              stopTimer: false,
            };
          }
          return q;
        }),
      );
    }
  }, [stopTimer]);

  useEffect(() => {
    if (resetTags) {
      handleUndo();
    }
  }, [resetTags]);

  const renderUnknown = () => {
    if (answer === unknown) {
      return (
        <Answer width={unknownWidth}>
          {hasUnknown ?
            <ButtonIcon color='#9aa7ba' marginRight={5} /> :
            <Progress
              type='circle'
              percent={timerPercent}
              width={15}
              showInfo={false}
              strokeWidth={12}
              trailColor='transparent'
              strokeColor='#9aa7ba'
            />
          }
          {intl.formatMessage({ id: 'ticket.UNKNOWN' })}
        </Answer>
      );
    } else if (answer === no) {
      return (
        <Undo onClick={handleUndo} width={unknownWidth}>
          <UndoIcon type='undo-alt' solid={true} color='#0098cc' />
          {intl.formatMessage({ id: 'ticket.UNDO' })}
        </Undo>
      );
    }
    return <Answer width={unknownWidth} />;
  };

  return (
    <Container
      jcsb
      aic
      ffrn
      visible={visible}
      isFirst={isFirst}
      isLast={isLast}
    >
      <Question hasUnknown={hasUnknown}>
        {question}
        {description ?
          <Tooltip title={description} placement='bottom'>
            <TooltipContainer><InfoIcon /></TooltipContainer>
          </Tooltip> :
          null
        }
      </Question>
      <Buttons jcfe aic hasUnknown={hasUnknown}>
        {!answer ?
          <>
            <div ref={yesRef}>
              <StyledButton color='#389F0C' onClick={() => handleAnswer(yes)} marginRight={10}>
                <ButtonIcon type='check' color='#389F0C' />
                {intl.formatMessage({ id: 'common.YES' })}
              </StyledButton>
            </div>
            <div ref={noRef}>
              <StyledButton
                color='#D4390D'
                onClick={() => handleAnswer(no)}
                marginRight={hasUnknown ? 10 : 0}
              >
                <ButtonIcon type='close' color='#D4390D' />
                {intl.formatMessage({ id: 'common.NO' })}
              </StyledButton>
            </div>
            {hasUnknown ?
              <div ref={unknownRef}>
                <StyledButton
                  color='#0098cc'
                  onClick={() => handleAnswer(unknown)}
                  marginRight={0}
                >
                  <ButtonIcon type='question' color='#0098cc' />
                  {intl.formatMessage({ id: 'ticket.UNKNOWN' })}
                </StyledButton>
              </div> :
              null
            }
          </> :
          <>
            {/* eslint-disable-next-line */}
            {answer === yes ?
              <Answer width={yesWidth}>
                {hasUnknown ?
                  <ButtonIcon type='check' color='#9aa7ba' marginRight={5} /> :
                  <Progress
                    type='circle'
                    percent={timerPercent}
                    width={15}
                    showInfo={false}
                    strokeWidth={12}
                    trailColor='transparent'
                    strokeColor='#9aa7ba'
                  />
                }
                {intl.formatMessage({ id: 'common.YES' })}
              </Answer> :
              !hasUnknown ?
                <Undo onClick={handleUndo}>
                  <UndoIcon type='undo-alt' solid={true} color='#0098cc' />
                  {intl.formatMessage({ id: 'ticket.UNDO' })}
                </Undo> :
                <Answer />
            }
            {answer === no ?
              <Answer width={noWidth}>
                {hasUnknown ?
                  <ButtonIcon type='close' color='#9aa7ba' marginRight={5} /> :
                  <Progress
                    type='circle'
                    percent={timerPercent}
                    width={15}
                    showInfo={false}
                    strokeWidth={12}
                    trailColor='transparent'
                    strokeColor='#9aa7ba'
                  />
                }
                {intl.formatMessage({ id: 'common.NO' })}
              </Answer> :
              <Undo onClick={handleUndo}>
                <UndoIcon type='undo-alt' solid={true} color='#0098cc' />
                {intl.formatMessage({ id: 'ticket.UNDO' })}
              </Undo>
            }
            {hasUnknown ? renderUnknown() : null}
          </>
        }
      </Buttons>
    </Container>
  );
};
QuestionBlock.propTypes = {
  intl: PropTypes.object,
  question: PropTypes.string,
  description: PropTypes.string,
  handleQuestionAnswer: PropTypes.func,
  handleQuestionUndo: PropTypes.func,
  yes: PropTypes.string,
  no: PropTypes.string,
  removeQuestion: PropTypes.func,
  id: PropTypes.string,
  undoTime: PropTypes.number,
  setQuestions: PropTypes.func,
  stopTimer: PropTypes.bool,
  hasUnknown: PropTypes.bool,
  unknown: PropTypes.string,
  resetTags: PropTypes.bool,
  visible: PropTypes.bool,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  canUndo: PropTypes.bool,
};

export default QuestionBlock;
